import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function Footer() {
  const { t } = useTranslation('navigation');
  const footerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !contentRef.current) return;

    const cols = contentRef.current.querySelectorAll('.footer-col');
    gsap.fromTo(
      cols,
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out' }
    );
  }, [isVisible]);

  return (
    <footer ref={footerRef as React.RefObject<HTMLElement>} className="bg-gradient-to-br from-[#1A3EB8] via-[#1535a0] to-[#0f2580] text-white py-16 relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
          backgroundSize: '30px 30px',
        }}
      />

      <div ref={contentRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="footer-col md:col-span-2" style={{ opacity: 0 }}>
            <h3 className="text-2xl font-bold mb-2">Alpha Solutions</h3>
            <p className="text-[#F57C20] text-sm font-semibold mb-4">{t('footerTagline')}</p>
            <p className="text-blue-100 leading-relaxed mb-4">
              {t('footerDescription')}
            </p>
            <div className="flex items-center gap-2 text-sm text-blue-200 mb-2">
              <i className="ri-map-pin-line text-[#F57C20]"></i>
              <span>{t('footerAddress')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-blue-200 mb-2">
              <i className="ri-phone-line text-[#F57C20]"></i>
              <span>+237 693 38 52 67</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-blue-200">
              <i className="ri-mail-line text-[#F57C20]"></i>
              <span>info@alphasolutions237.com</span>
            </div>
          </div>

          {/* Services */}
          <div className="footer-col" style={{ opacity: 0 }}>
            <h4 className="text-lg font-semibold mb-4">{t('ourServices')}</h4>
            <ul className="space-y-2">
              <li>
                <a href="/solutions/erp" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('erpSystems')}
                </a>
              </li>
              <li>
                <a href="/solutions/crm" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('crmSystems')}
                </a>
              </li>
              <li>
                <a href="/solutions" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('accountingSoftware')}
                </a>
              </li>
              <li>
                <a href="/solutions" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('hrSoftware')}
                </a>
              </li>
              <li>
                <a href="/solutions" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('industrySpecific')}
                </a>
              </li>
              <li>
                <a href="/solutions" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('technologyIntegration')}
                </a>
              </li>
            </ul>
          </div>

          {/* Links */}
          <div className="footer-col" style={{ opacity: 0 }}>
            <h4 className="text-lg font-semibold mb-4">{t('usefulLinks')}</h4>
            <ul className="space-y-2">
              <li>
                <a href="/" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('home')}
                </a>
              </li>
              <li>
                <a href="/solutions" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('solutions')}
                </a>
              </li>
              <li>
                <a href="/about" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('about')}
                </a>
              </li>
              <li>
                <a href="/recrutement" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('recruitment')}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-blue-100 hover:text-white hover:translate-x-1 transition-all duration-200 cursor-pointer inline-block">
                  {t('contact')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-blue-600 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-blue-200 text-sm">
            {t('footerCopyright')} {t('allRightsReserved')}.
          </p>

          <div className="flex gap-6">
            <a href="https://www.facebook.com/share/1CdTFucw18/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-white hover:scale-110 transition-all duration-200 cursor-pointer">
              <i className="ri-facebook-fill text-2xl"></i>
            </a>
            <a href="#" className="text-blue-200 hover:text-white hover:scale-110 transition-all duration-200 cursor-pointer">
              <i className="ri-linkedin-fill text-2xl"></i>
            </a>
            <a href="#" className="text-blue-200 hover:text-white hover:scale-110 transition-all duration-200 cursor-pointer">
              <i className="ri-twitter-fill text-2xl"></i>
            </a>
            <a href="#" className="text-blue-200 hover:text-white hover:scale-110 transition-all duration-200 cursor-pointer">
              <i className="ri-instagram-fill text-2xl"></i>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
